import { CODE_LENGTH } from "@/utils/auth-utils";
import { useEffect, useRef } from "react";

/**
 * 6 boxes for the SMS / email code. The code can arrive whole:
 *  - iPhone: the keyboard suggests it from the SMS (autocomplete="one-time-code")
 *    and drops all 6 digits into the focused box;
 *  - Android Chrome: read straight from the SMS (WebOTP — the SMS ends with
 *    "@diriyahhomes.com #123456"), no tap needed;
 *  - paste.
 * onComplete fires once all digits are in (submit without pressing the button).
 */
export default function AuthCodeField({
  value,
  onChange,
  onComplete,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (code: string) => void;
  disabled?: boolean;
}) {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = Array.from(
    { length: CODE_LENGTH },
    (_, index) => value?.[index] || "",
  );

  // Latest callbacks for the WebOTP listener (set up once).
  const cb = useRef({ onChange, onComplete });
  useEffect(() => {
    cb.current = { onChange, onComplete };
  });

  const updateValue = (nextDigits: string[]) => {
    const next = nextDigits.join("").slice(0, CODE_LENGTH);
    onChange(next);
    if (next.length === CODE_LENGTH && /^\d+$/.test(next)) onComplete?.(next);
  };

  /** Several digits at once (autofill / paste) → spread from `start`. */
  const fillFrom = (start: number, incoming: string) => {
    const nextDigits = [...digits];
    incoming.split("").forEach((d, i) => {
      if (start + i < CODE_LENGTH) nextDigits[start + i] = d;
    });
    updateValue(nextDigits);
    inputRefs.current[Math.min(start + incoming.length, CODE_LENGTH - 1)]?.focus();
  };

  // Android Chrome: take the code straight from the SMS.
  useEffect(() => {
    if (typeof window === "undefined" || !("OTPCredential" in window)) return;
    const ac = new AbortController();
    navigator.credentials
      .get({ otp: { transport: ["sms"] }, signal: ac.signal } as CredentialRequestOptions)
      .then((cred) => {
        const code = String((cred as { code?: string } | null)?.code || "").replace(/\D/g, "").slice(0, CODE_LENGTH);
        if (code.length !== CODE_LENGTH) return;
        cb.current.onChange(code);
        cb.current.onComplete?.(code);
      })
      .catch(() => {
        /* closed / not allowed — typing still works */
      });
    return () => ac.abort();
  }, []);

  return (
    <div className="mb-6 flex items-center justify-center gap-3" dir="ltr">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(element) => {
            inputRefs.current[index] = element;
          }}
          type="text"
          inputMode="numeric"
          // Only the first box offers the keyboard's "from Messages" code.
          autoComplete={index === 0 ? "one-time-code" : "off"}
          disabled={disabled}
          value={digit}
          onChange={(event) => {
            const typed = event.target.value.replace(/\D/g, "");
            // The whole code (keyboard suggestion) — maybe after a digit already there.
            if (typed.length >= CODE_LENGTH) return fillFrom(0, typed.slice(-CODE_LENGTH));
            if (typed.length > 2) return fillFrom(index, typed);
            const nextValue = typed.slice(-1);
            const nextDigits = [...digits];
            nextDigits[index] = nextValue;
            updateValue(nextDigits);

            if (nextValue && index < CODE_LENGTH - 1) {
              inputRefs.current[index + 1]?.focus();
            }
          }}
          onKeyDown={(event) => {
            if (event.key === "Backspace" && !digits[index] && index > 0) {
              inputRefs.current[index - 1]?.focus();
            }
          }}
          onPaste={(event) => {
            event.preventDefault();
            const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, CODE_LENGTH);
            if (pasted) fillFrom(pasted.length === CODE_LENGTH ? 0 : index, pasted);
          }}
          className={`h-13 w-11 rounded-xl border text-center text-lg font-bold text-neutral-900 outline-none transition-all duration-200 caret-transparent dark:text-white ${
            digit
              ? "border-clay-600 bg-clay-600/5 text-clay-600"
              : "border-neutral-200 bg-neutral-50 hover:border-neutral-300 dark:border-neutral-700 dark:bg-zinc-800 dark:hover:border-neutral-600"
          } focus:border-clay-600 focus:bg-white focus:shadow-[0_0_0_3px_rgb(var(--brand-rgb)/0.1)] dark:focus:bg-zinc-700`}
          aria-label={`code-digit-${index + 1}`}
        />
      ))}
    </div>
  );
}
